/* eslint-disable quote-props */
import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid } from 'recharts';
import dayjs from 'dayjs';
import { FragmentType, graphql, useFragment as getFragmentData } from '@frontend/gql/social';
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartLegendContent,
  ChartLegend,
} from '@frontend/shadcn/components/ui/chart';
import { Period } from '@frontend/gql/social/graphql';
import { formatNumber } from '../../../utils/formatNumber';
import { ChartTooltipContent } from '../../../components/chart';
import { formatDateByPrecision } from '../../utils';

const ReachChartFragment = graphql(`
  fragment ReachChart_MetaInsights on MetaInsights {
    interval {
      date
    }
    reach
  }
`);

type ReachChartProps = {
  currentIntervalsIntervalInsights: (FragmentType<typeof ReachChartFragment> | null | undefined)[] | null | undefined;
  currentIntervalsIntervalInsightsNotAspireSourced:
    | (FragmentType<typeof ReachChartFragment> | null | undefined)[]
    | null
    | undefined;
  showNonAttributedData?: boolean;
  precision: Period;
};

const ReachChart = ({
  currentIntervalsIntervalInsights,
  currentIntervalsIntervalInsightsNotAspireSourced,
  showNonAttributedData = false,
  precision,
}: ReachChartProps) => {
  const currentIntervalData =
    currentIntervalsIntervalInsights?.map((interval) => getFragmentData(ReachChartFragment, interval)) || [];

  const currentIntervalNotAspireSourcedData =
    currentIntervalsIntervalInsightsNotAspireSourced?.map((interval) =>
      getFragmentData(ReachChartFragment, interval),
    ) || [];

  // Transform data for the chart
  const data = currentIntervalData.map((interval, index) => {
    const notAspireSourced = currentIntervalNotAspireSourcedData[index];
    const date = interval?.interval?.date ? dayjs(interval.interval.date, 'YYYY-MM-DD').toDate() : new Date();
    const baseData = {
      month: formatDateByPrecision(date, precision),
      'Attributed to Aspire': interval?.reach || 0,
    };

    if (showNonAttributedData) {
      return {
        ...baseData,
        'Not Attributed to Aspire': notAspireSourced?.reach || 0,
      };
    }

    return baseData;
  });

  const chartConfig: ChartConfig = {
    'Attributed to Aspire': {
      label: 'Attributed to Aspire',
      color: 'hsl(var(--refresh-blue-4))',
    },
    ...(showNonAttributedData && {
      'Not Attributed to Aspire': {
        label: 'Not Attributed to Aspire',
        color: 'hsl(var(--refresh-blue-3))',
      },
    }),
  };

  return (
    <ChartContainer className="h-[300px]" config={chartConfig}>
      <LineChart data={data}>
        <CartesianGrid vertical={false} />
        <XAxis dataKey="month" tickLine={false} tickMargin={10} axisLine={false} tickFormatter={(value) => value} />
        <YAxis tickLine={false} axisLine={false} tickFormatter={(value: number) => formatNumber(value).toString()} />
        <ChartTooltip
          content={<ChartTooltipContent valueFormatter={(item) => `${formatNumber(Number(item.value)).toString()}`} />}
        />
        <ChartLegend content={<ChartLegendContent />} />
        <Line
          type="linear"
          dataKey="Attributed to Aspire"
          stroke="hsl(var(--refresh-blue-4))"
          strokeWidth={2}
          dot={false}
        />
        {showNonAttributedData && (
          <Line
            type="linear"
            dataKey="Not Attributed to Aspire"
            stroke="hsl(var(--refresh-blue-3))"
            strokeWidth={2}
            dot={false}
          />
        )}
      </LineChart>
    </ChartContainer>
  );
};

export default ReachChart;
